import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import {
  adminListUsers,
  adminSetUserBlocked,
  adminDeleteUser,
} from "@/lib/api/admin-users.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldAlert, Trash2, Lock, Unlock, Loader2, Search, UserCheck,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/admin/users")({
  head: () => ({
    meta: [
      { title: "User management — Admin | Nkadimeng Danny Legal & Compliance" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminUsers,
});

type Filter = "all" | "blocked" | "admins" | "trial";

function AdminUsers() {
  const list = useServerFn(adminListUsers);
  const block = useServerFn(adminSetUserBlocked);
  const del = useServerFn(adminDeleteUser);
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<Filter>("all");
  const [blockTarget, setBlockTarget] = useState<any | null>(null);
  const [blockReason, setBlockReason] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteReason, setDeleteReason] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", search, page],
    queryFn: () => list({ data: { search, page } }),
  });

  const blockMut = useMutation({
    mutationFn: (vars: { userId: string; blocked: boolean; reason?: string }) =>
      block({ data: vars }),
    onSuccess: (r) => {
      toast.success(r.blocked ? "User blocked." : "User unblocked.");
      setBlockTarget(null);
      setBlockReason("");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (vars: { userId: string; confirmEmail: string; reason?: string }) =>
      del({ data: vars }),
    onSuccess: () => {
      toast.success("Account permanently deleted.");
      setDeleteTarget(null);
      setDeleteConfirm("");
      setDeleteReason("");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const filtered = useMemo(() => {
    const rows = data?.users ?? [];
    if (filter === "blocked") return rows.filter((u: any) => u.blocked);
    if (filter === "admins") return rows.filter((u: any) => u.roles.includes("admin"));
    if (filter === "trial")
      return rows.filter(
        (u: any) =>
          u.trial_ends_at && new Date(u.trial_ends_at).getTime() > Date.now(),
      );
    return rows;
  }, [data, filter]);

  return (
    <section className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-navy">
            <UserCheck className="h-6 w-6" /> User management
          </h1>
          <p className="text-sm text-muted-foreground">
            View every registered account. Block users who violate the rules
            and regulations, or permanently delete their account if necessary.
          </p>
        </div>
        <div className="flex gap-2">
          {(["all", "blocked", "admins", "trial"] as Filter[]).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              onClick={() => setFilter(f)}
            >
              {f}
            </Button>
          ))}
        </div>
      </header>

      <ProspectiveLearnersBanner />

      <div className="flex gap-2">

        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by email, name, phone, or student number"
            className="pl-8"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading users…</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase">
              <tr>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Roles</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Subscription</th>
                <th className="px-3 py-2">Last sign-in</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                    No users match.
                  </td>
                </tr>
              ) : null}
              {filtered.map((u: any) => {
                const isAdmin = u.roles.includes("admin");
                return (
                  <tr key={u.id} className={u.blocked ? "bg-rose-50/60" : ""}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-navy">
                        {u.full_name || "—"}
                      </div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                      {u.student_number && (
                        <div className="text-[11px] font-mono text-muted-foreground">
                          {u.student_number}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {u.roles.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        u.roles.map((r: string) => (
                          <span
                            key={r}
                            className={
                              "mr-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase " +
                              (r === "admin"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-slate-100 text-slate-700")
                            }
                          >
                            {r}
                          </span>
                        ))
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {u.blocked ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-800">
                          <Lock className="h-3 w-3" /> Blocked
                        </span>
                      ) : u.email_confirmed_at ? (
                        <span className="text-emerald-700">Active</span>
                      ) : (
                        <span className="text-amber-700">Email unconfirmed</span>
                      )}
                      {u.trial_ends_at &&
                        new Date(u.trial_ends_at).getTime() > Date.now() && (
                          <div className="text-[11px] text-muted-foreground">
                            Trial → {new Date(u.trial_ends_at).toLocaleDateString("en-ZA")}
                          </div>
                        )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {u.subscription ? (
                        <>
                          <div>{u.subscription.plan_code ?? "—"}</div>
                          <div className="text-muted-foreground">
                            {u.subscription.status}
                            {u.subscription.current_period_end &&
                              ` · ${new Date(u.subscription.current_period_end).toLocaleDateString("en-ZA")}`}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">None</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {u.last_sign_in_at
                        ? new Date(u.last_sign_in_at).toLocaleString("en-ZA")
                        : <span className="text-muted-foreground">Never</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {u.blocked ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={blockMut.isPending}
                            onClick={() =>
                              blockMut.mutate({ userId: u.id, blocked: false })
                            }
                          >
                            <Unlock className="mr-1 h-3.5 w-3.5" /> Unblock
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isAdmin}
                            title={isAdmin ? "Cannot block an admin" : "Block this user"}
                            onClick={() => setBlockTarget(u)}
                          >
                            <Lock className="mr-1 h-3.5 w-3.5" /> Block
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={isAdmin}
                          title={isAdmin ? "Cannot delete an admin" : "Delete account"}
                          onClick={() => setDeleteTarget(u)}
                        >
                          <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Page {page}</span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={(data?.users.length ?? 0) < (data?.perPage ?? 50)}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Block dialog */}
      <Dialog open={!!blockTarget} onOpenChange={(v) => !v && setBlockTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-600" /> Block user
            </DialogTitle>
            <DialogDescription>
              The user will be immediately signed out and prevented from signing
              back in. They keep their data and can be unblocked at any time.
            </DialogDescription>
          </DialogHeader>
          {blockTarget && (
            <div className="space-y-3 text-sm">
              <p>
                <strong>{blockTarget.full_name || blockTarget.email}</strong>
                <br />
                <span className="text-muted-foreground">{blockTarget.email}</span>
              </p>
              <div>
                <Label htmlFor="block-reason">Reason (optional, audited)</Label>
                <Textarea
                  id="block-reason"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="e.g. Repeated abusive messages in CCMA matter NDL-2611-A1B2C3"
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={blockMut.isPending}
              onClick={() =>
                blockMut.mutate({
                  userId: blockTarget.id,
                  blocked: true,
                  reason: blockReason || undefined,
                })
              }
            >
              {blockMut.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Block user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-5 w-5" /> Permanently delete account
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The auth record and all rows linked
              to this user via ON DELETE CASCADE will be removed. POPIA records
              you must retain (orders, invoices) remain intact.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-3 text-sm">
              <p>
                You are about to delete{" "}
                <strong>{deleteTarget.full_name || deleteTarget.email}</strong>
                {" "}({deleteTarget.email}).
              </p>
              <div>
                <Label htmlFor="del-confirm">
                  Retype the user's email to confirm
                </Label>
                <Input
                  id="del-confirm"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder={deleteTarget.email}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="del-reason">Reason (audited)</Label>
                <Textarea
                  id="del-reason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Verified fraudulent registration; user requested deletion under POPIA Art 24"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={
                deleteMut.isPending ||
                !deleteTarget ||
                deleteConfirm.trim().toLowerCase() !==
                  (deleteTarget.email ?? "").toLowerCase()
              }
              onClick={() =>
                deleteMut.mutate({
                  userId: deleteTarget.id,
                  confirmEmail: deleteConfirm.trim(),
                  reason: deleteReason || undefined,
                })
              }
            >
              {deleteMut.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Permanently delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ProspectiveLearnersBanner() {
  // Lazy-import the server fn so we don't add extra bundles to the main users page.
  const [state, setState] = useState<{ loading: boolean; total: number; new: number }>({
    loading: true, total: 0, new: 0,
  });
  useMemo(() => {
    (async () => {
      try {
        const mod = await import("@/lib/api/founding-partners.functions");
        const call = mod.adminListProspectiveLearners as any;
        const res = await call({ data: {} });
        const rows = res?.rows ?? res?.learners ?? res ?? [];
        const list = Array.isArray(rows) ? rows : [];
        setState({
          loading: false,
          total: list.length,
          new: list.filter((r: any) => (r.status ?? "new") === "new").length,
        });
      } catch {
        setState({ loading: false, total: 0, new: 0 });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-sm flex items-center justify-between gap-3">
      <div>
        <div className="font-semibold text-emerald-900">
          Prospective learners (partner-referred, pre-registration)
        </div>
        <div className="text-xs text-emerald-800/80">
          {state.loading
            ? "Loading learner pipeline…"
            : `${state.total} learners on record · ${state.new} awaiting first contact. They appear as full accounts here once they register.`}
        </div>
      </div>
      <a
        href="/portal/admin/prospective-learners"
        className="rounded-md border border-emerald-600 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-600 hover:text-white"
      >
        Open learner pipeline →
      </a>
    </div>
  );
}

